import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useState,
} from "react";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { addToArray, removeFromArray, reorderArray } from "../../helper";

export interface DragCallbackParams<T = any> {
  type: "reorder" | "remove" | "add";
  sourceIndex: number;
  destinationIndex: number;
  item: T | null;
  sourceId: string;
  destinationId: string;
}

export interface DragContextType<T = any> {
  registerReorderCallback: (
    listId: string,
    callback: (params: DragCallbackParams<T>) => void
  ) => () => void;
  setDragData: (data: T) => void;
  isDragging: boolean;
}

export const DragContext = createContext<DragContextType | null>(null);

export const useDragContext = () => {
  const context = useContext(DragContext);
  if (!context) {
    throw new Error("useDragContext must be used within a DragProvider");
  }
  return context;
};

export const useDraggableList = <T,>(
  initialItems: T[]
): [
  T[],
  (params: DragCallbackParams<T>) => void,
  React.Dispatch<React.SetStateAction<T[]>>,
] => {
  const [items, setItems] = useState(initialItems);

  const handleListChange = useCallback(
    ({ type, sourceIndex, destinationIndex, item }: DragCallbackParams<T>) => {
      switch (type) {
        case "reorder":
          setItems((prev) => reorderArray(prev, sourceIndex, destinationIndex));
          break;
        case "remove":
          setItems((prev) => removeFromArray(prev, sourceIndex));
          break;
        case "add":
          setItems((prev) => addToArray(prev, destinationIndex, item));
          break;
      }
    },
    []
  );

  return [items, handleListChange, setItems];
};

export interface DragProviderProps {
  children: React.ReactNode;
}

export const DragProvider = <T,>({ children }: DragProviderProps) => {
  // Store callbacks keyed by listId
  const listCallbacks = useRef<
    Record<string, (params: DragCallbackParams<T>) => void>
  >({});
  const currentDragData = useRef<T | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const registerReorderCallback = useCallback(
    (listId: string, callback: (params: DragCallbackParams<T>) => void) => {
      listCallbacks.current[listId] = callback;
      return () => {
        delete listCallbacks.current[listId];
      };
    },
    []
  );

  const setDragData = useCallback((data: T) => {
    currentDragData.current = data;
  }, []);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback((result: DropResult) => {
    setIsDragging(false);
    const { source, destination } = result;

    if (!destination) {
      currentDragData.current = null;
      return;
    }

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      currentDragData.current = null;
      return;
    }

    const dragInfo = {
      sourceIndex: source.index,
      destinationIndex: destination.index,
      item: currentDragData.current,
      sourceId: source.droppableId,
      destinationId: destination.droppableId,
    };

    // Source callback
    const sourceCallback = listCallbacks.current[source.droppableId];
    if (sourceCallback) {
      sourceCallback({
        ...dragInfo,
        type:
          source.droppableId === destination.droppableId ? "reorder" : "remove",
      });
    }

    // Destination callback (if different)
    if (source.droppableId !== destination.droppableId) {
      const destinationCallback =
        listCallbacks.current[destination.droppableId];
      if (destinationCallback) {
        destinationCallback({
          ...dragInfo,
          type: "add",
        });
      }
    }

    currentDragData.current = null;
  }, []);

  return (
    <DragContext.Provider
      value={{ registerReorderCallback, setDragData, isDragging }}
    >
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {children}
      </DragDropContext>
    </DragContext.Provider>
  );
};
